// src/components/PortalVerificacao/SecaoDadosCadastro.tsx
// UPDATED: 2026-06-01 — v2.0 visual redesign, lógica preservada

import React, { useState } from 'react';
import { User, MapPin, Briefcase, Phone, Mail, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { VerificacaoData, formatDate } from '../../services/verificacaoService';

const BRAND = { gold: '#D4AF37', goldDeep: '#B88917', charcoal: '#1F2328' };

interface CampoProps { label: string; valor: string | null | undefined; icon?: React.ReactNode; }
const Campo: React.FC<CampoProps> = ({ label, valor, icon }) => {
  if (!valor) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '11px 0',
        borderBottom: '1px solid #f3f4f6',
      }}
    >
      {icon && (
        <div style={{ marginTop: 1, flexShrink: 0, color: '#9ca3af', display: 'flex' }}>
          {icon}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: 10,
            fontWeight: 700,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {label}
        </p>
        <p
          style={{
            margin: '3px 0 0',
            fontSize: 13.5,
            fontWeight: 600,
            color: BRAND.charcoal,
            wordBreak: 'break-word',
            lineHeight: 1.4,
          }}
        >
          {valor}
        </p>
      </div>
    </div>
  );
};

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  accent?: string;
}
const Section: React.FC<SectionProps> = ({ title, icon, children, defaultOpen = true, accent }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        borderRadius: 16,
        border: `1px solid ${accent ?? BRAND.gold}22`,
        background: 'white',
        overflow: 'hidden',
      }}
    >
      <button
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          border: 'none',
          background: open ? `${accent ?? BRAND.gold}08` : 'white',
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        onClick={() => setOpen(!open)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ color: accent ?? BRAND.goldDeep, display: 'flex', flexShrink: 0 }}>{icon}</div>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: BRAND.charcoal }}>{title}</span>
        </div>
        {open
          ? <ChevronUp   style={{ width: 16, height: 16, color: '#9ca3af' }} />
          : <ChevronDown style={{ width: 16, height: 16, color: '#9ca3af' }} />}
      </button>
      {open && (
        <div style={{ padding: '0 18px 4px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

interface Props { data: VerificacaoData; }

const SecaoDadosCadastro: React.FC<Props> = ({ data }) => {
  const META = data.metadata ?? {};

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.98)',
        borderRadius: 24,
        border: `1px solid ${BRAND.gold}28`,
        padding: '1.5rem',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}
    >
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: `${BRAND.gold}18`,
            border: `1.5px solid ${BRAND.gold}33`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <User style={{ width: 22, height: 22, color: BRAND.goldDeep }} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: BRAND.charcoal, letterSpacing: '-0.2px' }}>
            Dados do Cadastro
          </h3>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6b7280' }}>
            Informações registradas no formulário de filiação
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* Identificação */}
        <Section title="Identificação" icon={<User style={{ width: 16, height: 16 }} />}>
          <Campo label="Nome / Escola" valor={data.schoolName} />
          <Campo label="Responsável"   valor={data.responsibleName} />
          <Campo label="ID do Sistema" valor={data.systemId} />
          <Campo
            label="Data de Nascimento"
            valor={data.dataNascimento ? formatDate(data.dataNascimento) : null}
            icon={<Calendar style={{ width: 14, height: 14 }} />}
          />
          <Campo label="Cadastrado em" valor={formatDate(data.createdAt)} />
        </Section>

        {/* Contato */}
        {(data.phone || data.whatsapp || data.email) && (
          <Section
            title="Contato"
            icon={<Phone style={{ width: 16, height: 16 }} />}
            accent="#3b82f6"
          >
            <Campo label="Telefone"  valor={data.phone}    icon={<Phone style={{ width: 14, height: 14 }} />} />
            <Campo label="WhatsApp"  valor={data.whatsapp} icon={<Phone style={{ width: 14, height: 14 }} />} />
            <Campo label="E-mail"    valor={data.email}    icon={<Mail  style={{ width: 14, height: 14 }} />} />
          </Section>
        )}

        {/* Localização */}
        {(data.city || data.state) && (
          <Section
            title="Localização"
            icon={<MapPin style={{ width: 16, height: 16 }} />}
            defaultOpen={false}
            accent="#10b981"
          >
            <Campo label="Cidade"      valor={data.city}       icon={<MapPin style={{ width: 14, height: 14 }} />} />
            <Campo label="Estado"      valor={data.state} />
            {META.complemento && <Campo label="Complemento" valor={META.complemento} />}
          </Section>
        )}

        {/* Dados Profissionais */}
        {(META.funcao || META.experienciaAnos != null || META.dataInicioAtividade || META.especialidades?.length) && (
          <Section
            title="Dados Profissionais"
            icon={<Briefcase style={{ width: 16, height: 16 }} />}
            defaultOpen={false}
            accent="#8b5cf6"
          >
            <Campo label="Função / Atuação"    valor={META.funcao} icon={<Briefcase style={{ width: 14, height: 14 }} />} />
            <Campo label="Anos de Experiência" valor={META.experienciaAnos != null ? `${META.experienciaAnos} anos` : null} />
            <Campo label="Início na Área"      valor={META.dataInicioAtividade ? formatDate(META.dataInicioAtividade) : null} />
            {META.especialidades && META.especialidades.length > 0 && (
              <div style={{ padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Especialidades
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {META.especialidades.map((esp: any, i: number) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        padding: '4px 12px',
                        borderRadius: 99,
                        background: `${BRAND.gold}18`,
                        color: BRAND.goldDeep,
                        border: `1px solid ${BRAND.gold}33`,
                      }}
                    >
                      {esp}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
};

export default SecaoDadosCadastro;